// AuthHero — the shared brand panel for the auth family (sign-in, sign-up,
// forgot-password). The signature --ap-fire gradient with the real PlacementOS
// logo (same /logo.png as the navbar + footer) and a set of floating, slightly
// tilted "3D" product cards (score ring, keyword match, ATS pass) for an
// engaging, on-brand feel. Desktop-only (hidden < lg); pages show a compact
// logo header on mobile.
import { Link } from "react-router-dom";
import { CheckCircle2, ShieldCheck, TrendingUp, Sparkles } from "lucide-react";

function ScoreRing({ value = 92, stroke = "var(--ap-violet)" }) {
  const R = 22, C = 2 * Math.PI * R;
  return (
    <svg viewBox="0 0 56 56" className="w-14 h-14 -rotate-90 shrink-0" aria-hidden="true">
      <circle cx="28" cy="28" r={R} fill="none" stroke="var(--ap-line)" strokeWidth="5" />
      <circle cx="28" cy="28" r={R} fill="none" stroke={stroke} strokeWidth="5"
        strokeDasharray={C} strokeDashoffset={C - (value / 100) * C} strokeLinecap="round" />
    </svg>
  );
}

export default function AuthHero() {
  return (
    <div
      className="hidden lg:flex flex-col justify-between w-[46%] relative overflow-hidden p-12 text-white"
      style={{ background: "var(--ap-fire)" }}
    >
      {/* ambient depth orbs */}
      <div className="ap-float absolute -top-28 -right-16 w-96 h-96 rounded-full pointer-events-none" style={{ background: "rgba(255,255,255,.10)" }} />
      <div className="ap-float-2 absolute top-1/3 -left-24 w-72 h-72 rounded-full pointer-events-none" style={{ background: "rgba(255,255,255,.08)" }} />
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(120% 85% at 100% 0%, rgba(255,255,255,.14), transparent 55%)" }} />

      {/* logo — identical asset to navbar/footer */}
      <Link to="/" className="relative z-10 self-start" data-testid="brand-home">
        <img src="/logo.png" alt="PlacementOS" className="h-10 w-auto drop-shadow-[0_4px_14px_rgba(0,0,0,.18)]" />
      </Link>

      {/* headline */}
      <div className="relative z-10 max-w-[340px]">
        <span className="ap-eyebrow" style={{ color: "rgba(255,255,255,.9)" }}>Your career, decoded</span>
        <h1 className="ap-display text-[38px] leading-[1.1] font-extrabold mt-3">
          The honest ATS score. Then everything to fix it.
        </h1>
      </div>

      {/* social proof + trust */}
      <div className="relative z-10 space-y-3 max-w-[340px]">
        <div className="flex items-center gap-3">
          <div className="flex -space-x-2.5">
            {["#6B5CE6", "#9A59C4", "#C75C8E", "#F0734E"].map((c, i) => (
              <span key={i} className="w-8 h-8 rounded-full border-2 border-white/70" style={{ background: c }} />
            ))}
          </div>
          <span className="text-white/85 text-sm font-medium">Join 15,000+ professionals landing interviews</span>
        </div>
        <div className="flex items-center gap-2 text-white/65 text-xs">
          <ShieldCheck className="w-4 h-4" /> Your resume is analyzed privately and never shared.
        </div>
      </div>

      {/* ── floating "3D" product cards ─────────────────────────────────── */}
      {/* score ring card */}
      <div className="ap-float ap-glass absolute right-9 top-[20%] rounded-2xl p-4 flex items-center gap-3.5 rotate-[-5deg] z-20" style={{ width: 218 }}>
        <ScoreRing value={92} />
        <div>
          <p className="text-[10px] uppercase tracking-wider text-[var(--ap-muted)] font-semibold">Your resume</p>
          <p className="ap-display text-2xl font-extrabold text-[var(--ap-ink)] leading-none mt-0.5">92<span className="text-sm text-[var(--ap-muted)] font-semibold">/100</span></p>
          <p className="text-[11px] text-[var(--ap-green)] font-semibold mt-0.5 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Interview-ready</p>
        </div>
      </div>

      {/* keyword-match card */}
      <div className="ap-float-2 ap-glass absolute right-16 top-[45%] rounded-xl px-4 py-3 flex items-center gap-3 rotate-[5deg] z-20" style={{ animationDelay: ".6s" }}>
        <div className="w-9 h-9 rounded-lg grid place-items-center shrink-0" style={{ background: "var(--ap-violet-soft)" }}>
          <TrendingUp className="w-[18px] h-[18px] text-[var(--ap-violet)]" />
        </div>
        <div>
          <p className="ap-display text-base font-extrabold text-[var(--ap-ink)] leading-none">+18 keywords</p>
          <p className="text-[11px] text-[var(--ap-muted)] mt-0.5">matched to your target role</p>
        </div>
      </div>

      {/* ATS-pass pill */}
      <div className="ap-float ap-glass absolute right-8 top-[66%] rounded-full pl-2 pr-4 py-2 flex items-center gap-2 rotate-[-3deg] z-20" style={{ animationDelay: "1.1s" }}>
        <span className="w-7 h-7 rounded-full grid place-items-center shrink-0" style={{ background: "rgba(22,163,74,.14)" }}>
          <CheckCircle2 className="w-4 h-4 text-[var(--ap-green)]" />
        </span>
        <span className="text-[13px] font-bold text-[var(--ap-ink)]">ATS parsed cleanly</span>
      </div>
    </div>
  );
}
